import { useEffect, useMemo, useState } from 'react';
import { TIMESHEET_STATUSES, type TimesheetStatus } from '@catalyst-psa/shared';
import type { CwTimesheetStatusAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { STATUS_LABELS } from '../timesheets/status.js';
import { useCwTimesheetStatusAliases, useSetCwTimesheetStatusAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwStatusName: string;
  status: TimesheetStatus | '';
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `ts-status-alias-${aliasSeq}`;
}

interface TimesheetStatusAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise timesheet status (a dropdown) onto a PSA timesheet status (a
 * dropdown). Each row links a CW status name to one of the fixed PSA timesheet
 * statuses. On the time-entries sync, the CW timesheet status carried on an entry sets
 * the synced timesheet's status through these rows; a timesheet whose CW status has no
 * row defaults to Approved (synced timesheets are locked from editing in PSA), so the
 * sync never fails on a missing alias.
 */
export function TimesheetStatusAliasEditor({
  isOpen,
}: TimesheetStatusAliasEditorProps): JSX.Element {
  const configQuery = useCwTimesheetStatusAliases(isOpen);
  const saveAliases = useSetCwTimesheetStatusAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwStatusName: alias.cwStatusName,
        status: alias.status,
      })),
    );
  }, [config]);

  const discoveredStatuses = config?.discoveredStatuses ?? [];
  const live = config?.live ?? false;

  // CW status names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwStatusName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredStatuses.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwStatusName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwStatusName, status: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwTimesheetStatusAliasDto[] = rows
      .filter((row): row is AliasRow & { status: TimesheetStatus } =>
        Boolean(row.cwStatusName.trim() && row.status),
      )
      .map((row) => ({
        cwStatusName: row.cwStatusName.trim(),
        status: row.status,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-timesheet-statuses';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Timesheet status (dropdown)</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise groups time entries onto weekly timesheets with their own status. Map each
          ConnectWise timesheet status to a PSA timesheet status below. On sync, the matched status
          is set on the entry&apos;s weekly timesheet — ConnectWise statuses without a row here
          default to Approved.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading timesheet statuses…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load timesheet statuses.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise timesheet statuses loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise timesheet statuses discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredStatuses.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the timesheet-status aliases. Make sure each row targets a valid
              timesheet status.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No timesheet-status aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise status</th>
                  <th className="py-2 pr-3 font-medium">PSA timesheet status</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwStatusName}
                        placeholder="e.g. Approved"
                        onChange={(e) => updateRow(row.id, { cwStatusName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.status}
                        onChange={(e) =>
                          updateRow(row.id, { status: e.target.value as TimesheetStatus | '' })
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select status —</option>
                        {TIMESHEET_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save timesheet statuses
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
